import { CompareToDuration, CompareToType, IndicatorDuration, IndicatorType } from './agent-stat-enums';
import { AgentStatIndicator } from './models/agent-stat.model';
import { bcEnums } from '../../../common/common/constants/boldchat-enums';

export class AgentStatsHelper {

  static calculatePerformancePercentage(target: number, current: number): number {
    if (target > 0) {
      return Math.round(current / target * 100);
    } return null;
  }

  static getTextsBasedOnIndicatorType(indicatorType: IndicatorType) {
    switch (indicatorType) {
      case IndicatorType.NUMBER_OF_CLOSED_CHATS:
        return {
          shortName: 'agentStats.numberOfClosedChat.shortName',
          title: 'agentStats.numberOfClosedChat.title',
          subtitle: 'agentStats.numberOfClosedChat.subtitle',
          unit: 'agentStats.numberOfClosedChat.unit',
          valuePostFix: 'agentStats.numberOfClosedChat.valuePostFix',
          targetPostFix: 'agentStats.numberOfClosedChat.targetPostFix'
        };
      case IndicatorType.CONVERSION_COUNT:
        return {
          shortName: 'agentStats.numberOfConversions.shortName',
          title: 'agentStats.numberOfConversions.title',
          subtitle: 'agentStats.numberOfConversions.subtitle',
          unit: 'agentStats.numberOfConversions.unit',
          valuePostFix: 'agentStats.numberOfConversions.valuePostFix',
          targetPostFix: 'agentStats.numberOfConversions.targetPostFix'
        };
      case IndicatorType.CONVERSION_AMOUNT:
        return {
          shortName: 'agentStats.valueOfConversions.shortName',
          title: 'agentStats.valueOfConversions.title',
          subtitle: 'agentStats.valueOfConversions.subtitle',
          valuePostFix: 'agentStats.valueOfConversions.valuePostFix',
          targetPostFix: 'agentStats.valueOfConversions.targetPostFix'
        };
      case IndicatorType.SURVEY_NPS:
        return {
          shortName: 'agentStats.nps.shortName',
          title: 'agentStats.nps.title',
          subtitle: 'agentStats.nps.subtitle',
          valuePostFix: 'agentStats.nps.valuePostFix'
        };
      case IndicatorType.SURVEY_RESPONSIVENESS:
        return {
          shortName: 'agentStats.responsiveness.shortName',
          title: 'agentStats.responsiveness.title',
          subtitle: 'agentStats.responsiveness.subtitle',
          valuePostFix: 'agentStats.responsiveness.valuePostFix'
        };
      case IndicatorType.SURVEY_PROFESSIONALISM:
        return {
          shortName: 'agentStats.professionalism.shortName',
          title: 'agentStats.professionalism.title',
          subtitle: 'agentStats.professionalism.subtitle',
          valuePostFix: 'agentStats.professionalism.valuePostFix'
        };
      case IndicatorType.SURVEY_KNOWLEDGE:
        return {
          shortName: 'agentStats.knowledge.shortName',
          title: 'agentStats.knowledge.title',
          subtitle: 'agentStats.knowledge.subtitle',
          valuePostFix: 'agentStats.knowledge.valuePostFix'
        };
      case IndicatorType.SURVEY_JUST_OVERALL:
        return {
          shortName: 'agentStats.overall.shortName',
          title: 'agentStats.overall.title',
          subtitle: 'agentStats.overall.subtitle',
          valuePostFix: 'agentStats.overall.valuePostFix'
        };
      default:
        return;
    }
  }

  static convertIndicatorCompareToDuration(compareToDuration: CompareToDuration): string {
    switch (compareToDuration) {
      case CompareToDuration.YESTERDAY:
        return 'agentStats.compareToDuration.yesterday';
      case CompareToDuration.LAST_WEEK:
        return 'agentStats.compareToDuration.lastWeek';
      case CompareToDuration.LAST_30_DAYS:
        return 'agentStats.compareToDuration.lastThirtyDays:';
      default:
        throw new Error(`Trying to convert an unknown compareTo duration: (${compareToDuration})`);
    }
  }

  static convertIndicatorDuration(indicatorDuration: IndicatorDuration): string {
    switch (indicatorDuration) {
      case IndicatorDuration.TODAY:
        return 'agentStats.indicatorDuration.today';
      case IndicatorDuration.THIS_WEEK:
        return 'agentStats.indicatorDuration.thisWeek';
      default:
        throw new Error(`Trying to convert an unknown indicator duration: (${indicatorDuration})`);
    }
  }

  static isComparison(indicator: AgentStatIndicator): boolean {
    switch (indicator.indicatorType) {
      case IndicatorType.NUMBER_OF_CLOSED_CHATS:
      case IndicatorType.CONVERSION_AMOUNT:
      case IndicatorType.CONVERSION_COUNT:
      case IndicatorType.SURVEY_JUST_OVERALL:
      case IndicatorType.SURVEY_KNOWLEDGE:
      case IndicatorType.SURVEY_PROFESSIONALISM:
      case IndicatorType.SURVEY_RESPONSIVENESS:
      case IndicatorType.SURVEY_NPS:
        return indicator.compareToType !== CompareToType.NONE && +indicator.compareToValue > 0;
      default:
        return false;
    }
  }

  static convertIndicator(indicator: AgentStatIndicator) {
    const convertedIndicator = {
      indicatorType: AgentStatsHelper._convertIndicatorType(indicator.indicatorType),
      serviceType: AgentStatsHelper._convertServiceType(indicator.serviceType),
      orderId: indicator.orderId,
      indicatorDuration: AgentStatsHelper._convertIndicatorDuration(indicator.indicatorDuration),
      compareToType: AgentStatsHelper._convertCompareToType(indicator.compareToType),
      compareToDuration: AgentStatsHelper._convertCompareToDuration(indicator.compareToDuration),
      compareToValue: indicator.compareToValue
    };
    return convertedIndicator;
  }

  private static _convertServiceType(serviceType: number): string {
    switch (serviceType) {
      case bcEnums.services.CHATS:
        return 'Chat';
      default:
        return '';
    }
  }

  private static _convertIndicatorType(indicatorType: IndicatorType): string {
    switch (indicatorType) {
      case IndicatorType.NUMBER_OF_CLOSED_CHATS:
        return 'NumberOfClosedChats';
      case IndicatorType.CONVERSION_AMOUNT:
        return 'ValueOfConversions';
      case IndicatorType.CONVERSION_COUNT:
        return 'NumberOfConversions';
      case IndicatorType.SURVEY_JUST_OVERALL:
        return 'SurveyOverall';
      case IndicatorType.SURVEY_KNOWLEDGE:
        return 'SurveyKnowledge';
      case IndicatorType.SURVEY_PROFESSIONALISM:
        return 'SurveyProfessionalism';
      case IndicatorType.SURVEY_RESPONSIVENESS:
        return 'SurveyResponsiveness';
      case IndicatorType.SURVEY_NPS:
        return 'NPS';
      default:
        return '';
    }
  }

  private static _convertIndicatorDuration(indicatorDuration: IndicatorDuration): string {
    switch (indicatorDuration) {
      case IndicatorDuration.THIS_WEEK:
        return 'ThisWeek';
      case IndicatorDuration.TODAY:
        return 'Today';
      default:
        return '';
    }
  }

  private static _convertCompareToType(compareToType: CompareToType): string {
    switch (compareToType) {
      case CompareToType.NONE:
        return 'None';
      case CompareToType.TARGET_VALUE:
        return 'TargetValue';
      default:
        return '';
    }
  }

  private static _convertCompareToDuration(compareToDuration: CompareToDuration): string {
    switch (compareToDuration) {
      case CompareToDuration.LAST_30_DAYS:
        return 'Last30Days';
      case CompareToDuration.LAST_WEEK:
        return 'LastWeek';
      case CompareToDuration.YESTERDAY:
        return 'Yesterday';
      default:
        return '';
    }
  }
}
